import { z } from 'zod';
import { DebtTrackerSchema } from '../customer';
import { PaymentMethodDataSchema } from '../customer/payment-methods';
import { ZodStringToDate } from '../system/msc';
/**
 * BillingControlsSchema
 *
 * Groups all billing-related configuration settings.
 * Nullable to indicate when billing controls are not applicable.
 *
 * @property taxEnabled - Whether taxes should be calculated and applied
 * @property processingFeesEnabled - Whether processing fees should be calculated
 * @property clientPaysFees - Whether the client pays processing fees (true) or merchant absorbs them (false)
 */
export const BillingControlsSchema = z
    .object({
    taxEnabled: z.boolean(),
    processingFeesEnabled: z.boolean(),
    clientPaysFees: z.boolean(),
})
    .nullable();
/**
 * InvoiceControlsSchema
 *
 * Groups all invoice-specific configuration settings.
 * Nullable - only applicable when salesChannel is INVOICE.
 *
 * @property displayLogo - Whether to display business logo on invoice
 * @property dueDate - Invoice payment due date
 * @property terms - Payment terms (e.g., "Net 30", "Due on receipt")
 * @property lateFeesEnabled - Whether to apply late fees for overdue invoices
 */
export const InvoiceControlsSchema = z
    .object({
    displayLogo: z.boolean(),
    dueDate: z.date().or(ZodStringToDate).nullable(),
    terms: z.string(),
    lateFeesEnabled: z.boolean(),
})
    .nullable();
/**
 * NotesSchema
 *
 * Structured notes at three levels for different audiences.
 *
 * @property client - Notes visible to the client/customer
 * @property merchant - Internal notes for merchant use only
 * @property platform - System/platform-level notes for debugging or automation
 */
export const NotesSchema = z.object({
    client: z.string().nullable(),
    merchant: z.string().nullable(),
    platform: z.string().nullable(),
});
/**
 * SaleCustomerSchema
 *
 * Customer information for a sale.
 * Only id and name are required, all other fields are optional.
 * This allows storing partial customer data in the sale context.
 */
export const SaleCustomerSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    email: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),
    address: z.string().optional().nullable(),
    debtTracker: DebtTrackerSchema.nullable().optional(),
    paymentMethods: PaymentMethodDataSchema.array().nullable().optional(),
})
    .nullable();
/**
 * OrderCustomerSchema
 *
 * Complete customer information associated with an order.
 * Based on CustomerDataSchema but simplified for order context.
 */
export const OrderCustomerSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    phone: z.string(),
    address: z.string(),
    debtTracker: DebtTrackerSchema.nullable().optional(),
    paymentMethods: PaymentMethodDataSchema.array().nullable().optional(),
});
/**
 * OrderSchema
 *
 * Full order information including customer details and order metadata.
 * Nullable - is only applicable when the sale is associated with an order.
 *
 * @property number - Unique order number/identifier
 * @property requiresProcessing - Whether this order requires fulfillment/processing
 * @property status - Order status (e.g., 'pending', 'processing', 'completed', 'cancelled')
 * @property createdAt - When the order was created
 * @property updatedAt - When the order was last updated
 * @property customer - Complete customer information for this order (nullable)
 * @property data - Flexible object to store additional order-specific data
 */
/** @deprecated Use SaleOrderDetailsSchema for sale metadata. */
export const OrderSchema = z
    .object({
    number: z.string(),
    requiresProcessing: z.boolean(),
    status: z.string(),
    createdAt: z.date().or(ZodStringToDate),
    updatedAt: z.date().or(ZodStringToDate),
    customer: OrderCustomerSchema.nullable(),
    data: z.record(z.unknown()),
})
    .nullable();
export var SalesChannelEnum;
(function (SalesChannelEnum) {
    SalesChannelEnum["POS"] = "POS";
    SalesChannelEnum["INVOICE"] = "INVOICE";
    SalesChannelEnum["MOBILE_SERVICE"] = "MOBILE_SERVICE";
    SalesChannelEnum["WEB_PAYMENT"] = "WEB_PAYMENT";
    SalesChannelEnum["RECURRING_PAYMENT"] = "RECURRING_PAYMENT";
    SalesChannelEnum["PAYMENT_METHOD_ATTACHMENT"] = "PAYMENT_METHOD_ATTACHMENT";
    SalesChannelEnum["PAYMENT_LINK"] = "PAYMENT_LINK";
})(SalesChannelEnum || (SalesChannelEnum = {}));
export const SalesChannelSchema = z.nativeEnum(SalesChannelEnum);
